import { useState } from "react";
import { OrganisationFeatureFlags } from "shared";
import { SparklesIcon } from "@heroicons/react/20/solid";

import { Badge } from "../../../common/Atoms/Badge";
import { Button } from "../../../common/Atoms/Button";
import { useContactsV2 } from "../../hooks/useContacts";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useMobileNumberCheck } from "../../hooks/useMobileNumberCheck";
import { useModal } from "../../hooks/useModals";
import { MobileNumberFixModalContent } from "../Modals/MobileNumberFixModal";
import { useStartMobileNumberCheckMutation } from "../../services/api/mobileNumberCheckApi/mobileNumberCheck";

/** Queues a background check of every contact person's mobile number and opens the progress modal (mounted in Root) */
export function CheckMobileNumbersButton() {
  const currentOrg = useGetSelectedOrganisation();
  const { runningCheck } = useMobileNumberCheck();
  const { show } = useModal(`mobileNumberCheck`);
  const [startCheck, { isLoading }] = useStartMobileNumberCheckMutation();

  async function onClick() {
    // A running check is never restarted, the button just reopens its progress
    if (!runningCheck) {
      const result = await startCheck(currentOrg.id);

      if (`error` in result) {
        return;
      }
    }

    show();
  }

  return (
    <Button
      onClick={ onClick }
      size={ `sm` }
      color={ `white` }
      disabled={ !currentOrg || isLoading }
      loading={ isLoading }
    >
      { runningCheck ? `Checking mobile numbers...` : `Check all mobile numbers` }
    </Button>
  );
}

/** The AI fix is part of the AI assistant feature */
export function useAiMobileFixEnabled() {
  return !!useGetSelectedOrganisation()?.featureFlags?.includes(OrganisationFeatureFlags.AI_ASSISTANT);
}

/** Badge sized button shown beside an invalid mobile number, the caller opens the single contact AI fix */
export function InvalidMobileFixButton({ onClick, size = `xs` }: { onClick: () => void; size?: `xs` | `sm` }) {
  if (!useAiMobileFixEnabled()) {
    return (
      <Badge
        color={ `red` }
        message={ `Invalid` }
        className={ `ml-2` }
      />
    );
  }

  return (
    <button
      type={ `button` }
      className={ `ml-2 align-middle` }
      title={ `Suggest the correct format for this contact's invalid mobile numbers` }
      onClick={ event => {
        // Table rows navigate on click
        event.stopPropagation();
        onClick();
      } }
    >
      <Badge
        color={ `red` }
        size={ size }
        className={ `inline-flex! gap-1 cursor-pointer hover:bg-red-100` }
      >
        { `Invalid · Fix with AI` }
        <SparklesIcon className={ size === `sm` ? `h-4 w-4` : `h-3.5 w-3.5` } />
      </Badge>
    </button>
  );
}

/** Org wide: opens the AI fix modal mounted in Root, which suggests corrections for every mobile number the check flagged */
export function FixMobileNumbersButton() {
  const { show } = useModal(`mobileNumberFix`);

  if (!useAiMobileFixEnabled()) {
    return null;
  }

  return (
    <Button
      onClick={ show }
      size={ `sm` }
      color={ `white` }
    >
      <SparklesIcon className={ `mr-1 h-4 w-4` } />
      { `Fix mobile numbers with AI` }
    </Button>
  );
}

/** Contact table: the same fix, only for the selected contacts. Its modal first lists which of them will be checked */
export function FixSelectedMobileNumbersButton({ selectedContacts }: { selectedContacts: number[] }) {
  const [open, setOpen] = useState(false);
  // Same cache entry as the table
  const { data } = useContactsV2();

  // ponytail: only knows contacts on the loaded page. A selection kept from another page is left out,
  // have the server work out the invalid ones if cross page selection becomes a thing
  const selection = (data?.contacts || [])
    .filter(contact => selectedContacts.includes(contact.id))
    .map(contact => ({
      id: contact.id,
      name: contact.name,
      hasInvalidMobile: [contact.primaryContactPerson, ...contact.contactPersons].some(person => person?.mobileNumberInvalid),
    }));

  if (!useAiMobileFixEnabled()) {
    return null;
  }

  return (
    <>
      <Button
        onClick={ () => setOpen(true) }
        size={ `sm` }
        color={ `white` }
        disabled={ !selectedContacts.length }
      >
        <SparklesIcon className={ `mr-1 h-4 w-4` } />
        { `Fix mobile numbers with AI` }
      </Button>
      <MobileNumberFixModalContent
        open={ open }
        hide={ () => setOpen(false) }
        selection={ selection }
      />
    </>
  );
}
